/**
 * Toast Notification System
 *
 * Manages toast messages with queueing and auto-dismissal
 * @module portal/modules/ui/toasts
 */

let toastIdCounter = 0;

interface Toast {
  id: string;
  message: string;
  variant: string;
  duration?: number;
}

interface ActiveToast {
  element: HTMLElement;
  timeoutId: number;
}

const toastState: {
  queue: Toast[];
  active: Map<string, ActiveToast>;
  limit: number;
  container: HTMLElement | null;
} = {
  queue: [],
  active: new Map(),
  limit: 3,
  container: null,
};

function ensureToastContainer(): HTMLElement | null {
  if (!toastState.container) {
    toastState.container = document.getElementById('toast-container');
  }
  return toastState.container;
}

function processToastQueue(): void {
  ensureToastContainer();
  if (!toastState.container) {
    return;
  }
  while (toastState.active.size < toastState.limit && toastState.queue.length) {
    const next = toastState.queue.shift();
    if (next) {
      mountToast(next);
    }
  }
}

/**
 * Dismiss a toast by ID
 */
export function dismissToast(id: string): void {
  const active = toastState.active.get(id);
  if (!active) {
    return;
  }
  toastState.active.delete(id);
  if (active.timeoutId) {
    clearTimeout(active.timeoutId);
  }
  const element = active.element;
  if (element) {
    element.classList.remove('toast-visible');
    window.setTimeout(() => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
      processToastQueue();
    }, 220);
  } else {
    processToastQueue();
  }
}

function mountToast(toast: Toast): void {
  const container = ensureToastContainer();
  if (!container) {
    return;
  }
  const variant = toast.variant || 'info';
  const toastEl = document.createElement('div');
  toastEl.className = `toast toast-${variant}`;
  toastEl.setAttribute('role', variant === 'error' ? 'alert' : 'status');
  toastEl.setAttribute('aria-live', variant === 'error' ? 'assertive' : 'polite');
  const messageEl = document.createElement('div');
  messageEl.className = 'toast-message';
  messageEl.textContent = toast.message;
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'toast-close';
  closeBtn.setAttribute('aria-label', 'Dismiss notification');
  closeBtn.textContent = '×';
  closeBtn.addEventListener('click', () => {
    dismissToast(toast.id);
  });
  toastEl.appendChild(messageEl);
  toastEl.appendChild(closeBtn);
  container.appendChild(toastEl);
  requestAnimationFrame(() => {
    toastEl.classList.add('toast-visible');
  });
  const baseDuration =
    toast.duration && Number.isFinite(toast.duration)
      ? toast.duration
      : variant === 'error'
        ? 7000
        : 5000;
  const duration = Math.max(2000, baseDuration);
  const timeoutId = window.setTimeout(() => {
    dismissToast(toast.id);
  }, duration);
  toastState.active.set(toast.id, { element: toastEl, timeoutId: timeoutId });
}

/**
 * Show a toast notification
 */
export function showToast(
  message: string,
  options?: { type?: string; variant?: string; duration?: number }
): string {
  const text = message != null ? String(message) : '';
  const toast = {
    id: `toast-${Date.now()}-${++toastIdCounter}`,
    message: text,
    variant: options?.type || options?.variant || 'info',
    duration: options?.duration,
  };
  toastState.queue.push(toast);
  processToastQueue();
  return toast.id;
}
